import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Check, ArrowRight, Tag, Loader2, Sparkles, Award } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import Footer from '@/components/Footer';

// CaliMoms Connect — 2-tier model: Connect (free) / Thrive ($39/mo · $390/yr)
const MONTHLY_PRICE = 39;
const ANNUAL_PRICE = 390;
const WORKSHOP_GUEST_PRICE = 45;

const CONNECT_FEATURES = [
  'Community access',
  'City discovery',
  'Limited AI matching',
  'Limited messaging',
  'Community marketplace',
  'All gatherings free',
  `Workshops at $${WORKSHOP_GUEST_PRICE}`,
];

const THRIVE_FEATURES = [
  'Everything in Connect',
  'Priority + enhanced AI matching',
  'Unlimited messaging',
  'Curated Circles (4-6 women)',
  'Members-only events',
  'Local business discounts',
  'All gatherings free',
  'ONE WORKSHOP INCLUDED EVERY MONTH',
  '30% off additional workshops',
  '24-hour early registration',
];

export default function PricingPage() {
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billingCycle, setBillingCycle] = useState('annual'); // annual is the recommended default
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null);
  const [percentPromo, setPercentPromo] = useState(null);
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) checkPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out');
      return;
    }
    setCheckingPayment(true);
    try {
      const response = await api.get(`/stripe/checkout/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        const userResponse = await api.get('/me');
        updateUser(userResponse.data);
        toast.success('Payment successful! Welcome to Thrive.');
        navigate('/dashboard');
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
      } else {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment check error:', error);
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleSelectConnect = async () => {
    if (!isAuthenticated) {
      navigate('/signup?tier=connect');
      return;
    }
    setLoading('connect');
    try {
      await api.post('/stripe/free-tier');
      const userResponse = await api.get('/me');
      updateUser(userResponse.data);
      toast.success("Welcome to Connect! You're all set.");
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate Connect tier');
    } finally {
      setLoading(null);
    }
  };

  const handleSelectThrive = async () => {
    if (!isAuthenticated) {
      toast.info('Please create an account first');
      navigate(`/signup?tier=thrive&billing=${billingCycle}`);
      return;
    }
    setLoading('thrive');
    try {
      const payload = {
        tier: 'thrive',
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
      };
      if (percentPromo) payload.promo_code = percentPromo.code;
      const response = await api.post('/stripe/checkout', payload);
      if (response.data.url) window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    if (!isAuthenticated) {
      toast.info('Please create an account first, then apply your promo code.');
      navigate(`/signup?promo=${promoCode.trim()}`);
      return;
    }
    setPromoLoading(true);
    try {
      const res = await api.post('/promo/apply', { code: promoCode.trim() });
      setPromoApplied(res.data);
      const userRes = await api.get('/me');
      updateUser(userRes.data);
      toast.success(res.data.message);
      setTimeout(() => navigate('/dashboard'), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const isPercentOffCode = /enter it on the pricing page during checkout/i.test(detail);
      if (isPercentOffCode) {
        let validated = null;
        let lastErr = null;
        const candidateKeys = ['thrive_founding', 'thrive_regular', 'thrive_monthly', 'connect_monthly'];
        for (const planKey of candidateKeys) {
          try {
            const r = await api.post('/promo/validate-percent', {
              code: promoCode.trim(),
              plan_key: planKey,
            });
            validated = r.data;
            break;
          } catch (e2) { lastErr = e2; }
        }
        if (validated) {
          setPercentPromo(validated);
          toast.success(`${validated.percent_off}% off applied — checkout to use it`);
        } else {
          toast.error(lastErr?.response?.data?.detail || 'Promo not valid for the current plan');
        }
      } else {
        toast.error(detail || 'Invalid promo code');
      }
    } finally {
      setPromoLoading(false);
    }
  };

  const displayPrice = billingCycle === 'annual' ? ANNUAL_PRICE : MONTHLY_PRICE;
  const periodLabel = billingCycle === 'annual' ? '/year' : '/month';

  const thriveDiscounted = useMemo(() => {
    if (!percentPromo) return null;
    const pct = percentPromo.percent_off || 0;
    return Math.round(displayPrice * (1 - pct / 100) * 100) / 100;
  }, [percentPromo, displayPrice]);

  const isThriveMember = ['thrive', 'premium', 'connected'].includes(user?.membership_tier);
  const isFoundingMember = Boolean(user?.is_founding_member);

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center font-body">
        <div className="text-center">
          <div className="animate-pulse text-teal text-2xl mb-4">Processing payment...</div>
          <p className="text-text-mid">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-white font-body">
      <nav className="py-6 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-display text-2xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          {isAuthenticated ? (
            <Link to="/dashboard" className="text-teal font-semibold hover:text-teal-deep">Dashboard</Link>
          ) : (
            <Link to="/login" className="text-teal font-semibold hover:text-teal-deep">Sign In</Link>
          )}
        </div>
      </nav>

      <section className="py-12 px-6 md:px-12 text-center" data-testid="pricing-header">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="font-display text-4xl md:text-5xl text-navy mb-4">
              Find Your <span className="italic-teal">Membership.</span>
            </h1>
            <p className="text-text-mid text-lg mb-2">Two simple plans. Pick what fits your journey.</p>
            {isFoundingMember && (
              <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 text-xs font-bold px-4 py-1.5 rounded-full mt-3" data-testid="founding-member-badge">
                <Award size={14} /> FOUNDING MEMBER — 48-hour early workshop registration, always
              </span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Monthly / Annual toggle */}
      <section className="pb-8 px-6" data-testid="billing-toggle-section">
        <div className="flex justify-center">
          <div className="inline-flex items-center bg-white border border-border-subtle rounded-full p-1 shadow-sm">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${billingCycle === 'monthly' ? 'bg-navy text-white' : 'text-text-mid hover:text-navy'}`}
              data-testid="billing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1.5 ${billingCycle === 'annual' ? 'bg-navy text-white' : 'text-text-mid hover:text-navy'}`}
              data-testid="billing-toggle-annual"
            >
              Annual
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${billingCycle === 'annual' ? 'bg-teal text-white' : 'bg-teal/10 text-teal'}`}>
                2 MONTHS FREE
              </span>
            </button>
          </div>
        </div>
        <p className="text-center text-xs text-text-mid mt-2">Annual is the best value — recommended</p>
      </section>

      <section className="pb-8 px-6 md:px-12" data-testid="pricing-cards">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Connect (Free) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-[32px] p-8 bg-white border border-border-subtle"
              data-testid="pricing-card-connect"
            >
              <span className="inline-block bg-teal/10 text-teal text-xs font-bold px-3 py-1 rounded-full mb-4">
                FREE FOREVER
              </span>
              <h3 className="font-display text-2xl mb-2 text-navy">Connect</h3>
              <p className="text-sm mb-6 text-text-mid">Free forever — no credit card required</p>
              <div className="mb-6 flex items-baseline gap-1">
                <span className="text-4xl font-bold text-navy">$0</span>
                <span className="text-sm text-text-light">/month</span>
              </div>
              <ul className="space-y-3 mb-8">
                {CONNECT_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={18} className="flex-shrink-0 mt-0.5 text-teal" />
                    <span className="text-sm text-text-mid">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleSelectConnect}
                disabled={loading === 'connect' || user?.membership_tier === 'connect'}
                data-testid="select-plan-connect"
                className="w-full rounded-full font-bold bg-teal text-white hover:bg-teal-deep"
              >
                {user?.membership_tier === 'connect' ? 'Current Plan' : loading === 'connect' ? 'Processing...' : (
                  <>Get Started Free <ArrowRight size={16} className="ml-2" /></>
                )}
              </Button>
            </motion.div>

            {/* Thrive */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="rounded-[32px] p-8 bg-navy text-white relative overflow-hidden"
              data-testid="pricing-card-thrive"
            >
              <span className="inline-flex items-center gap-1 bg-teal text-white text-xs font-bold px-3 py-1 rounded-full mb-4">
                <Sparkles size={12} /> {billingCycle === 'annual' ? 'BEST VALUE' : 'MOST POPULAR'}
              </span>
              <h3 className="font-display text-2xl mb-2 text-white">Thrive</h3>
              {/* Headline value benefit */}
              <p className="text-teal-100 font-semibold text-base mb-5 leading-snug" style={{ color: '#9ce0d6' }} data-testid="thrive-value-headline">
                Thrive costs less than a single workshop — and includes one every month.
              </p>
              <div className="mb-1">
                <div className="flex items-baseline gap-3 flex-wrap">
                  {percentPromo && thriveDiscounted != null ? (
                    <>
                      <span className="text-4xl font-bold text-white" data-testid="thrive-promo-price">
                        ${thriveDiscounted.toFixed(2)}
                      </span>
                      <span className="text-lg line-through text-white/60">${displayPrice}</span>
                      <span className="text-sm text-white/80">{periodLabel}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-white" data-testid="thrive-price">
                        ${displayPrice}
                      </span>
                      <span className="text-sm text-white/80">{periodLabel}</span>
                    </>
                  )}
                </div>
              </div>
              <p className="text-xs text-white/70 mb-6" data-testid="thrive-billing-note">
                {billingCycle === 'annual'
                  ? `$390/year — two months free vs. $${MONTHLY_PRICE}/month. Includes one guest pass per year.`
                  : `Or $${ANNUAL_PRICE}/year — two months free.`}
              </p>
              <ul className="space-y-3 mb-8">
                {THRIVE_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={18} className="flex-shrink-0 mt-0.5 text-white" />
                    <span className={`text-sm ${f.includes('WORKSHOP INCLUDED') ? 'text-white font-bold' : 'text-white/90'}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleSelectThrive}
                disabled={loading === 'thrive' || isThriveMember}
                data-testid="select-plan-thrive"
                className="w-full rounded-full font-bold bg-teal text-white hover:bg-teal-deep"
              >
                {isThriveMember ? 'Current Plan' : loading === 'thrive' ? 'Processing...' : (
                  <>Join Thrive <ArrowRight size={16} className="ml-2" /></>
                )}
              </Button>
            </motion.div>
          </div>

          {/* Value comparison module */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-6 mx-auto max-w-2xl bg-cream border border-border-subtle rounded-2xl p-6"
            data-testid="value-comparison-module"
          >
            <p className="text-center text-navy font-display text-lg mb-4">Do the math</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 text-center border border-border-subtle">
                <p className="text-xs uppercase tracking-wide text-text-mid mb-1">As a guest</p>
                <p className="text-2xl font-bold text-navy">${WORKSHOP_GUEST_PRICE}</p>
                <p className="text-xs text-text-mid mt-1">one workshop + gatherings</p>
              </div>
              <div className="bg-navy rounded-xl p-4 text-center">
                <p className="text-xs uppercase tracking-wide text-white/70 mb-1">With Thrive</p>
                <p className="text-2xl font-bold text-white">${MONTHLY_PRICE}<span className="text-sm font-normal text-white/70">/mo</span></p>
                <p className="text-xs text-white/80 mt-1">workshop included — plus everything else</p>
              </div>
            </div>
          </motion.div>

          <p className="mt-4 text-center text-sm text-text-mid" data-testid="referral-tagline">
            Refer a friend who joins Thrive and get a free month — share the love.
          </p>
        </div>
      </section>

      <section className="pb-12 px-6 md:px-12" data-testid="promo-section">
        <div className="max-w-xl mx-auto text-center">
          {promoApplied ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-teal/10 border border-teal/20 rounded-2xl p-6"
              data-testid="promo-applied-confirmation"
            >
              <Check size={32} className="text-teal mx-auto mb-2" />
              <p className="text-navy font-semibold mb-1">{promoApplied.message}</p>
              <p className="text-text-mid text-sm">Redirecting to your dashboard...</p>
            </motion.div>
          ) : (
            <>
              {!promoOpen ? (
                <button
                  onClick={() => setPromoOpen(true)}
                  className="text-teal text-sm font-medium hover:text-teal-deep transition-colors inline-flex items-center gap-1.5"
                  data-testid="promo-code-link"
                >
                  <Tag size={14} /> Have a promo code? Click here
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-white rounded-2xl shadow-card p-6 flex flex-col items-center"
                  data-testid="promo-code-form"
                >
                  <p className="text-navy font-semibold mb-3 text-center">Enter your promo code.</p>
                  <div className="flex gap-2 w-full max-w-sm">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="e.g. CMCVIP10"
                      className="rounded-xl font-mono text-center tracking-wider"
                      onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                      data-testid="promo-code-input"
                    />
                    <Button
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="bg-teal hover:bg-teal-deep text-white rounded-full px-6"
                      data-testid="apply-promo-btn"
                    >
                      {promoLoading ? <Loader2 size={16} className="animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                  <button
                    onClick={() => { setPromoOpen(false); setPromoCode(''); }}
                    className="text-xs text-text-light hover:text-text-mid mt-3 inline-block"
                  >
                    Cancel
                  </button>
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl text-navy mb-4">Questions?</h2>
          <p className="text-text-mid mb-6">
            Both plans include access to our community events calendar. Upgrade or downgrade anytime.
          </p>
          <a href="mailto:hello@calimomsconnect.com" className="text-teal font-semibold hover:text-teal-deep">
            Contact us at hello@calimomsconnect.com
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
